import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
    FiArrowLeft,
    FiChevronDown,
    FiChevronUp,
    FiBook,
    FiCheckCircle,
    FiEye,
    FiAward,
    FiStar
} from 'react-icons/fi';
import { API_BASE } from '../../../api';
import { ENDPOINTS as LMS_ENDPOINTS } from '../Utils/Endpoints';
import { Models as LmsModels } from '../Utils/ModelsLMS';
import { getSessionUserId } from '../../../utils/SessionUser';
import AlertDialog from '../Components/Shared/AlertDialog';
import '../Styles/LmsExtra.css';

const CarreraCurriculumStudent = () => {
    const { id: idCarrera } = useParams();
    const navigate = useNavigate();
    const SERVER_URL = API_BASE.replace(/\/api\/?$/, '');

    // Estados
    const [carrera, setCarrera] = useState(null);
    const [semestres, setSemestres] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [misCursos, setMisCursos] = useState([]);

    // UI
    const [expandedSemesters, setExpandedSemesters] = useState({});
    const [carreraEstudianteRow, setCarreraEstudianteRow] = useState(null);
    const [certGenerating, setCertGenerating] = useState(false);
    const [alertDialog, setAlertDialog] = useState({ open: false, title: "Información", message: "", variant: "info" });

    // Cargar datos iniciales
    const loadData = useCallback(async () => {
        setLoading(true);
        const idUsuarioSesion = getSessionUserId();

        try {
            // 0. Sincronizar Progreso (Auto-desbloqueo de niveles)
            if (idUsuarioSesion) {
                const resEst = await axios.get(LMS_ENDPOINTS.estudianteByUsuario(idUsuarioSesion));
                const studentData = resEst.data;
                const idEst = studentData.idEstudiante || studentData.IdEstudiante;
                
                if (idEst) {
                    console.log(`[CarreraCurriculumStudent] 🔄 Iniciando sincronización de carrera: ID ${idCarrera} para estudiante: ${idEst}...`);
                    const resSync = await axios.post(LMS_ENDPOINTS.carreraEstudianteSyncProgreso, {
                        IdEstudiante: Number(idEst),
                        IdCarrera: Number(idCarrera)
                    });
                    console.log("[CarreraCurriculumStudent] ✅ Sincronización completada:", resSync.data);
                }
            }

            // 1. Obtener Carrera
            const resCarrera = await axios.get(LMS_ENDPOINTS.carrerasById(idCarrera));
            const carreraData = Array.isArray(resCarrera.data) ? resCarrera.data[0] : resCarrera.data;
            if (carreraData) {
                setCarrera(LmsModels.carrera(carreraData));
            }

            // 2. Obtener Mis Cursos (para progreso)
            if (idUsuarioSesion) {
                const [resMisCursos, resEstudiante] = await Promise.all([
                    axios.get(LMS_ENDPOINTS.misCursos(idUsuarioSesion)),
                    axios.get(LMS_ENDPOINTS.estudianteByUsuario(idUsuarioSesion))
                ]);

                const rawCursos = resMisCursos.data?.Cursos || resMisCursos.data?.cursos || (Array.isArray(resMisCursos.data) ? resMisCursos.data : []);
                setMisCursos(rawCursos.map(c => ({
                    idCurso: c.IdCurso ?? c.idCurso,
                    estado: (c.Estado ?? c.estado ?? 'en-curso').toLowerCase(),
                    progreso: c.Progreso ?? c.progreso ?? 0,
                    resultado: c.Resultado ?? c.resultado ?? '—',
                    calificacionFinal: c.CalificacionFinal ?? c.calificacionFinal,
                    certificadoGenerado: c.CertificadoGenerado ?? c.certificadoGenerado ?? false
                })));

                // Obtener registro de CarreraEstudiante
                if (resEstudiante.data) {
                    const resMisCarreras = await axios.get(LMS_ENDPOINTS.misCarreras(idUsuarioSesion));
                    const carrerasList = resMisCarreras.data?.Carreras || resMisCarreras.data?.carreras || [];
                    const myCarrera = carrerasList.find(c => Number(c.IdCarrera || c.idCarrera) === Number(idCarrera));
                    if (myCarrera) {
                        setCarreraEstudianteRow(myCarrera);
                    }
                }
            }

            // 3 & 4. Obtener Estructura de la Carrera (Semestres y Relaciones) - Incluyendo Histórico
            // Obtenemos el ID del estudiante para el filtro
            const resEst = await axios.get(LMS_ENDPOINTS.estudianteByUsuario(idUsuarioSesion));
            const idEst = resEst.data.idEstudiante || resEst.data.IdEstudiante;

            console.log(`[CarreraCurriculumStudent] 🔍 Solicitando estructura histórica: Carrera=${idCarrera}, Estudiante=${idEst}`);
            const resEstructura = await axios.get(LMS_ENDPOINTS.semestresHistoricoEstudiante(idCarrera, idEst));
            console.log("[CarreraCurriculumStudent] 📥 Respuesta Estructura RAW:", resEstructura.data);
            
            const rawStructure = resEstructura.data?.CarrerasEstructuraHistorica || resEstructura.data || {};
            const rawSemestres = rawStructure.Semestres || rawStructure.semestres || [];
            const rawRelaciones = rawStructure.Relaciones || rawStructure.relaciones || [];
            
            console.log(`[CarreraCurriculumStudent] 📊 Datos extraídos: ${rawSemestres.length} semestres, ${rawRelaciones.length} relaciones.`);

            const semestresFiltrados = (rawSemestres || [])
                .map(LmsModels.semestre)
                .sort((a, b) => (1 * a.idSemestre) - (1 * b.idSemestre));

            const semCursosData = (rawRelaciones || []).map(LmsModels.semestreCurso);

            const resTodosCursos = await axios.get(LMS_ENDPOINTS.cursosAdminHistorico);
            const todosCursos = (resTodosCursos.data || []).map(LmsModels.cursos);

            // 5. Catálogos para labels
            const [resNiveles, resHabilidades] = await Promise.all([
                axios.get(LMS_ENDPOINTS.catNivel),
                axios.get(LMS_ENDPOINTS.catTipoHabilidad)
            ]);
            const niveles = (resNiveles.data || []).map(LmsModels.niveles);
            const habilidades = (resHabilidades.data || []).map(LmsModels.tipoHabilidad);

            const semestresMapeados = semestresFiltrados.map(s => {
                const idS = s.idSemestre;
                return {
                    ...s,
                    cursos: semCursosData
                        .filter(sc => Number(sc.idSemestre) === Number(idS) && sc.activo)
                        .map(sc => {
                            const cursoInfo = todosCursos.find(c => Number(c.idCurso) === Number(sc.idCurso));
                            if (!cursoInfo) return null;

                            const nivel = niveles.find(n => n.idNivel === cursoInfo.idNivel);
                            const habilidad = habilidades.find(h => h.idTipoHabilidad === cursoInfo.idTipoHabilidad);

                            return {
                                idSemestreCurso: sc.idSemestreCurso,
                                idCurso: sc.idCurso,
                                nombre: cursoInfo.nombre,
                                imagen: cursoInfo.uRLRutaImagen,
                                nivel: nivel ? nivel.nombre : '-',
                                habilidad: habilidad ? habilidad.nombre : '-',
                                duracion: cursoInfo.duracion
                            };
                        }).filter(Boolean)
                };
            });

            setSemestres(semestresMapeados);

            console.log(`[DIAGNOSTICO] Relaciones Semestre-Curso: ${semCursosData.length}`);
            console.log(`[DIAGNOSTICO] Finalizado ObtenerHistoricoEstudiante_V2`);
            
            // Expandir TODOS los semestres por defecto
            const initialExpanded = {};
            semestresMapeados.forEach((s) => {
                initialExpanded[s.idSemestre] = true;
            });
            setExpandedSemesters(initialExpanded);

        } catch (err) {
            console.error('Error al cargar currículo de carrera para estudiante:', err);
            setError('No se pudo cargar la información de la carrera.');
        } finally {
            setLoading(false);
        }
    }, [idCarrera]);

    useEffect(() => {
        loadData();
    }, [idCarrera, loadData]);

    const toggleSemester = (id) => {
        setExpandedSemesters(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const getCursoProgreso = useCallback((idCurso) => {
        const matching = misCursos.find(c => Number(c.idCurso) === Number(idCurso));
        if (!matching) return { estado: 'no-inscrito', progreso: 0 };
        return matching;
    }, [misCursos]);

    const handleOpenCurso = (idCurso) => {
        const prog = getCursoProgreso(idCurso);
        if (prog.estado === 'no-inscrito') return;
        navigate(`/menu/lms/curso-estudiante/${idCurso}`);
    };

    const handleToggleAll = () => {
        const allExp = Object.values(expandedSemesters).every(v => v);
        const nextState = !allExp;
        const newState = {};
        semestres.forEach(s => newState[s.idSemestre] = nextState);
        setExpandedSemesters(newState);
    };

    const handleGenerateCertificate = async () => {
        if (certGenerating) return;
        setCertGenerating(true);
        try {
            const idUsuarioSesion = getSessionUserId();
            const resEstudiante = await axios.get(LMS_ENDPOINTS.estudianteByUsuario(idUsuarioSesion));
            const idEst = resEstudiante.data.idEstudiante || resEstudiante.data.IdEstudiante;

            const payload = {
                IdCarrera: Number(idCarrera),
                IdEstudiante: Number(idEst)
            };

            const response = await axios.post(LMS_ENDPOINTS.certificadoCarreraGenerateForStudent, payload);

            if (response.data?.success) {
                // Actualizar estado local
                setCarreraEstudianteRow(prev => ({ ...prev, CertificadoGenerado: true, certificadoGenerado: true }));
                setAlertDialog({
                    open: true,
                    title: "¡Felicidades!",
                    message: "Tu certificado de carrera ha sido generado exitosamente.",
                    variant: "success",
                });
            }
        } catch (error) {
            console.error('Error al generar certificado de carrera:', error);
            setAlertDialog({
                open: true,
                title: "Error",
                message: "Ocurrió un error al generar tu certificado. Por favor intenta más tarde.",
                variant: "error",
            });
        } finally {
            setCertGenerating(false);
        }
    };

    // Estadísticas calculadas
    const stats = useMemo(() => {
        const totalCursos = semestres.reduce((acc, s) => acc + s.cursos.length, 0);
        let completados = 0;
        let sumaProgreso = 0;
        let semestresCompletados = 0;

        semestres.forEach(s => {
            const cursoCount = s.cursos.length;
            if (cursoCount === 0) return;

            let completadosEnSemestre = 0;
            s.cursos.forEach(c => {
                const p = getCursoProgreso(c.idCurso);
                const isFinalizado = p.estado === 'aprobado' || p.estado === 'finalizado';
                if (isFinalizado) {
                    completados++;
                    completadosEnSemestre++;
                    sumaProgreso += 100;
                } else {
                    sumaProgreso += (p.progreso || 0);
                }
            });

            if (completadosEnSemestre === cursoCount) {
                semestresCompletados++;
            }
        });

        return {
            totalCursos,
            completados,
            semestresCompletados,
            semestresRestantes: semestres.length - semestresCompletados,
            promedioAvance: totalCursos > 0 ? Math.round(sumaProgreso / totalCursos) : 0
        };
    }, [semestres, getCursoProgreso]);

    if (loading) return <div className="cc-page"><p>Cargando trayectoria académica...</p></div>;
    if (error) return <div className="cc-page"><p style={{ color: '#dc2626' }}>{error}</p></div>;
    if (!carrera) return <div className="cc-page"><p>Carrera no encontrada.</p></div>;

    const allExpanded = Object.values(expandedSemesters).every(v => v);

    return (
        <div className="cc-page">
            <div className="cc-breadcrumb">
                <button
                    type="button"
                    className="cc-breadcrumb-back"
                    onClick={() => navigate("/menu/lms/mis-carreras")}
                >
                    <FiArrowLeft />
                    Volver a Mis Carreras
                </button>
            </div>

            <div className="cc-layout">
                <div className="cc-main">
                    <h1 className="cc-course-title">
                        {carrera.nombre}
                    </h1>

                    <div className="cc-chip-row">
                        <span className="cc-chip">
                            {semestres.length} niveles
                        </span>
                        <span className="cc-chip">
                            {stats.totalCursos} cursos
                        </span>
                        <span className="cc-chip">
                            {stats.completados} aprobados
                        </span>
                    </div>

                    <div className="cc-toolbar">
                        <button
                            type="button"
                            className="cc-add-lesson-btn"
                            onClick={handleToggleAll}
                        >
                            {allExpanded
                                ? "Contraer todas las secciones"
                                : "Expandir todas las secciones"}
                        </button>
                    </div>

                    <div className="cc-modules-list">
                        {semestres.map((semestre, idx) => {
                            const isExpanded = expandedSemesters[semestre.idSemestre];
                            const cursoCount = semestre.cursos.length;
                            let completadosCount = 0;
                            semestre.cursos.forEach(c => {
                                const p = getCursoProgreso(c.idCurso);
                                if (p.estado === 'aprobado' || p.estado === 'finalizado') completadosCount++;
                            });

                            const isCompletado = completadosCount === cursoCount && cursoCount > 0;
                            const semestreProgress = cursoCount > 0
                                ? Math.round(semestre.cursos.reduce((acc, c) => {
                                    const p = getCursoProgreso(c.idCurso);
                                    return acc + ((p.estado === 'aprobado' || p.estado === 'finalizado') ? 100 : (p.progreso || 0));
                                }, 0) / cursoCount)
                                : 0;

                            return (
                                <div key={semestre.idSemestre} className="cc-module-card">
                                    <div
                                        className="cc-module-header"
                                        onClick={() => toggleSemester(semestre.idSemestre)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="cc-module-header-left">
                                            <div className="cc-module-index-badge">{idx + 1}</div>
                                            <div>
                                                <h2 className="cc-module-title">
                                                    {semestre.nombre}
                                                </h2>
                                                <p className="cc-module-meta">
                                                    {cursoCount} {cursoCount === 1 ? 'curso' : 'cursos'} • {semestreProgress}% completado
                                                </p>
                                            </div>
                                        </div>

                                        <div className="cc-module-header-right">
                                            {isCompletado && (
                                                <FiAward style={{ color: '#16a34a', fontSize: '1.4rem', marginRight: '0.75rem' }} title="Nivel acreditado" />
                                            )}
                                            <button type="button" className="cc-module-toggle-btn">
                                                {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                                            </button>
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className="cc-lessons-list">
                                            {semestre.cursos.map((curso) => {
                                                const prog = getCursoProgreso(curso.idCurso);
                                                const isCompleted = prog.estado === 'aprobado' || prog.estado === 'finalizado';
                                                const isReprobado = prog.estado === 'reprobado';
                                                const isLocked = prog.estado === 'no-inscrito';

                                                let itemBgClass = "cc-item-neutral";
                                                if (isCompleted) itemBgClass = "cc-item-success";
                                                else if (isReprobado) itemBgClass = "cc-item-error";

                                                return (
                                                    <div
                                                        key={curso.idSemestreCurso}
                                                        className={`cc-lesson-row ${itemBgClass}`}
                                                    >
                                                        <div
                                                            className="cc-lesson-main"
                                                            onClick={() => !isLocked && handleOpenCurso(curso.idCurso)}
                                                            style={{ cursor: isLocked ? 'default' : 'pointer', flex: 1 }}
                                                        >
                                                            <div className="cc-lesson-icon" style={{ padding: 0, overflow: 'hidden', width: 44, height: 44, borderRadius: 8 }}>
                                                                {curso.imagen ? (
                                                                    <img
                                                                        src={curso.imagen.startsWith('http') ? curso.imagen : `${SERVER_URL}${curso.imagen}`}
                                                                        alt=""
                                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                    />
                                                                ) : (
                                                                    <FiBook style={{ padding: '8px' }} />
                                                                )}
                                                            </div>
                                                            <div className="cc-lesson-texts">
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                    <h3 className="cc-lesson-title">
                                                                        {curso.nombre}
                                                                    </h3>
                                                                    {isCompleted && <FiCheckCircle style={{ color: '#16a34a', fontSize: '1rem' }} />}
                                                                </div>
                                                                <p className="cc-lesson-meta">
                                                                    <span style={{ fontWeight: 600 }}>{curso.nivel}</span>
                                                                    <span style={{ margin: '0 6px', color: '#cbd5e1' }}>•</span>
                                                                    <span>{curso.habilidad}</span>
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="cc-lesson-actions" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                            {!isLocked && (
                                                                <div style={{ textAlign: 'right', minWidth: '80px' }}>
                                                                    <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                                                                        Nota
                                                                    </div>
                                                                    <div style={{
                                                                        fontSize: '1.1rem',
                                                                        fontWeight: 800,
                                                                        color: isReprobado ? '#ef4444' : (isCompleted ? '#16a34a' : '#0f172a'),
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'flex-end',
                                                                        gap: '4px'
                                                                    }}>
                                                                        {prog.calificacionFinal != null ? Number(prog.calificacionFinal).toFixed(1) : (isCompleted ? '10' : '-')}
                                                                        {isCompleted && <FiStar style={{ fontSize: '0.8rem', fill: '#16a34a' }} />}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {isLocked ? (
                                                                <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '600', padding: '0.25rem 0.5rem' }}>
                                                                    🔒 Bloqueado
                                                                </span>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    className="cc-lesson-btn cc-lesson-btn-ghost"
                                                                    onClick={() => handleOpenCurso(curso.idCurso)}
                                                                >
                                                                    <FiEye /> Ver
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <aside className="cc-sidebar">
                    <div className="cc-summary-card">
                        <div className="cc-summary-status-pill">
                            {stats.promedioAvance === 100 ? 'Completada' : 'En curso'}
                        </div>
                        <h3 className="cc-summary-title cc-summary-title-student">
                            Resumen de la carrera
                        </h3>

                        <div className="cc-summary-grid">
                            <div className="cc-summary-item">
                                <small className="cc-summary-label">NIVELES</small>
                                <strong className="cc-summary-value">{semestres.length}</strong>
                            </div>
                            <div className="cc-summary-item">
                                <small className="cc-summary-label">CURSOS</small>
                                <strong className="cc-summary-value">{stats.totalCursos}</strong>
                            </div>
                            <div className="cc-summary-item">
                                <small className="cc-summary-label">APROBADOS</small>
                                <strong className="cc-summary-value" style={{ color: '#16a34a' }}>{stats.completados}</strong>
                            </div>
                        </div>

                        <hr style={{ margin: '1.5rem 0', border: '0', borderTop: '1px solid #f1f5f9' }} />

                        <div style={{ textAlign: 'center', margin: '1.5rem 0' }}>
                            <div style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '0.5rem', fontWeight: '500', textTransform: 'uppercase' }}>
                                Niveles terminados
                            </div>
                            <div style={{ fontSize: '3rem', fontWeight: '700', color: '#0f172a', lineHeight: 1 }}>
                                {stats.semestresCompletados}/{semestres.length}
                            </div>
                            <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '0.5rem', fontWeight: '500' }}>
                                {stats.semestresRestantes > 0 ? `${stats.semestresRestantes} por terminar` : '¡Carrera completada!'}
                            </div>
                        </div>

                        <div style={{
                            textAlign: 'center',
                            color: '#64748b',
                            fontSize: '0.85rem',
                            marginBottom: '0.75rem',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.025em'
                        }}>
                            Progreso de la carrera
                        </div>

                        <div className="cc-summary-progress" style={{ marginTop: '0' }}>
                            <div className="cc-summary-bar">
                                <div
                                    className="cc-summary-bar-fill"
                                    style={{
                                        width: `${stats.promedioAvance}%`,
                                        transition: 'width 1s ease-in-out'
                                    }}
                                />
                            </div>
                            <div className="cc-summary-label cc-summary-progress-text" style={{ textAlign: 'center', marginTop: '0.75rem' }}>
                                {stats.completados} de {stats.totalCursos} completadas ({stats.promedioAvance}%)
                            </div>
                        </div>

                        {/* Botón de acción: Se oculta si ya se completó la carrera Y ya se generó el certificado */}
                        {!(stats.promedioAvance === 100 && (carreraEstudianteRow?.CertificadoGenerado || carreraEstudianteRow?.certificadoGenerado)) && (
                            <button
                                type="button"
                                className="cc-summary-btn-main"
                                disabled={certGenerating}
                                onClick={() => {
                                    if (stats.promedioAvance === 100) {
                                        handleGenerateCertificate();
                                    } else {
                                        navigate('/menu/lms/mis-carreras');
                                    }
                                }}
                            >
                                {stats.promedioAvance === 100
                                    ? (certGenerating ? 'Generando...' : 'Obtener Certificado')
                                    : 'Continuar carrera'}
                            </button>
                        )}
                    </div>
                </aside>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                .cc-item-success {
                    background-color: #f0fdf4 !important;
                    border: 1px solid #22c55e !important;
                }
                .cc-item-error {
                    background-color: #fef2f2 !important;
                    border: 1px solid #ef4444 !important;
                }
                .cc-item-neutral {
                    background-color: #ffffff !important;
                    border: 1px solid #e2e8f0 !important;
                }
            `}} />

            {/* Modal de alertas estándar LMS */}
            <AlertDialog
                open={alertDialog.open}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
                onClose={() => {
                    setAlertDialog({ ...alertDialog, open: false });
                    if (alertDialog.onClose) alertDialog.onClose();
                }}
                buttonText="Aceptar"
            />
        </div>
    );
};

export default CarreraCurriculumStudent;
